import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CompactTracking from './CompactTracking';

const TransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [rechargeCount, setRechargeCount] = useState(0);
  const [tokenChargeCount, setTokenChargeCount] = useState(0);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionsPerPage] = useState(10);
  const navigate = useNavigate();

  // Memoize fetch function to fix useEffect dependency warning
  const fetchTransactions = useCallback(async (page = 1) => {
    try {
      const token = localStorage.getItem("tokenly_token");
      if (!token) {
        setError("No authentication token found. Please login again.");
        navigate("/login");
        return;
      }

      setLoading(true);
      setError(null);
      
      const response = await fetch("https://tokenly.biswaas.com/api/user/transactionHistory", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          offset: page
        })
      });

      // Also fetch total counts for all types
      const statsResponse = await fetch("https://tokenly.biswaas.com/api/user/transactionHistory", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pageNo: 1,
          limit: 1000000 // Large number to get all transactions for stats
        })
      });

      if (!response.ok || !statsResponse.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Transaction API Response for offset', page, ':', result);
      console.log('Request body:', { offset: page });
      const statsResult = await statsResponse.json();
      
      // Calculate total recharge and token charge counts from all transactions
      let totalRecharge = 0;
      let totalTokenCharges = 0;
      
      if (statsResult.status && statsResult.data && statsResult.data.rows) {
        statsResult.data.rows.forEach((row) => {
          // Recharge transactions are credits (trans !== 'debit')
          if (row.trans !== 'debit') {
            totalRecharge++;
          }
          // Token charge transactions are debits
          else if (row.trans === 'debit') {
            totalTokenCharges++;
          }
        });
      }
      
      setRechargeCount(totalRecharge);
      setTokenChargeCount(totalTokenCharges);
      
      // Set wallet balance from the latest transaction (first in the array)
      if (result.status && result.data && result.data.rows && result.data.rows.length > 0) {
        const latestTransaction = result.data.rows[0];
        setWalletBalance(latestTransaction.user_balance || 0);
      }
      
      if (result.status && result.data && result.data.rows) {
        const mappedTransactions = result.data.rows
          .map((row) => ({
            id: row.id,
            date: row.transdate,
            time: row.transtime,
            type: row.title || row.payment_type || 'Transaction',
            amount: row.RechgAmt,
            isDebit: row.trans === 'debit',
            balance: row.user_balance,
            status: row.status === 1 ? 'Completed' : 'Pending',
            reference: row.ResponseString || row.OrderId || row.TxnID || `#${row.id.toString().slice(-6)}`,
            doctorName: row.trans === 'debit' ? (row.ResponseString || 'Transaction') : (row.TxnID || `#${row.id.toString().slice(-6)}`),
            tokenNo: row.token_no,
            createdAt: row.created_at
          }));
        
        setTransactions(mappedTransactions);
        setTotalRows(result.data.total_rows || 0);
        setCurrentPage(page);
      } else {
        setTransactions([]);
        setError(result.message || "No transactions found");
      }
    } catch (err) {
      setError(err.message);
      console.error('Transaction API error:', err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchTransactions(currentPage);
  }, [currentPage, fetchTransactions]);

  const handleRefresh = () => {
    fetchTransactions(1);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalRows / transactionsPerPage);
  const paginate = useCallback((pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  }, [totalPages]);

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}>
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <p className="text-center mt-3 h6 text-muted">Loading your transactions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger text-center p-4">
          <i className="fas fa-exclamation-triangle fa-2x mb-3 d-block"></i>
          <h5>Error loading transactions</h5>
          <p className="mb-4">{error}</p>
          <button className="btn btn-outline-danger me-2" onClick={handleRefresh}>
            <i className="fas fa-sync-alt me-1"></i>Retry
          </button>
          <button className="btn btn-outline-secondary" onClick={() => navigate("/")}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      {/* Hero Section with Background Image - Same as other pages */}
      <section className="contact-hero d-flex align-items-center">
        <div className="contact-hero-overlay"></div>
        <div className="container-fluid container-xl px-4 px-md-3">
          <h1 className="display-5 fw-bold text-white mb-1">Transaction <span className="text-warning">History</span></h1>
          <p className="text-light mb-0">
            All your token transactions and balance changes
          </p>
        </div>
      </section>
      <CompactTracking />

      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-lg-11 col-xl-10">
          {/* Stats Cards */}
          {transactions.length > 0 && (
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <div className="card border-0 shadow-lg bg-info text-white h-100 rounded-4">
                  <div className="card-body text-center">
                    <div className="bg-white bg-opacity-25 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                      <i className="fas fa-list fa-2x"></i>
                    </div>
                    <h3 className="h2 fw-bold mb-1 text-white">{totalRows.toLocaleString()}</h3>
                    <p className="mb-0 small fw-bold text-white">Total Recharge</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-lg bg-info text-white h-100 rounded-4">
                  <div className="card-body text-center">
                    <div className="bg-white bg-opacity-25 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                      <i className="fas fa-plus-circle fa-2x"></i>
                    </div>
                    <h3 className="h2 fw-bold mb-1 text-white">{rechargeCount.toLocaleString()}</h3>
                    <p className="mb-0 small fw-bold text-white">Used Balance</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-lg bg-success text-white h-100 rounded-4">
                  <div className="card-body text-center">
                    <div className="bg-white bg-opacity-25 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                      <span className="fa-2x fw-bold">₹</span>
                    </div>
                    <h3 className="h2 fw-bold mb-1 text-white">{walletBalance.toLocaleString()}</h3>
                    <p className="mb-0 small fw-bold text-white">Current Balance</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          {transactions.length === 0 ? (
            <div className="text-center py-5 my-5 bg-white rounded-4 shadow-sm">
              <div className="bg-light bg-opacity-50 rounded-circle d-inline-flex align-items-center justify-content-center mb-4" style={{width: '100px', height: '100px'}}>
                <i className="fas fa-receipt fa-3x text-muted"></i>
              </div>
              <h4 className="text-muted mb-3 fw-semibold">No transactions yet</h4>
              <p className="text-muted mb-4 lead">
                Your transaction history will appear here when you book tokens.
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <button className="btn btn-outline-primary rounded-pill px-4" onClick={() => navigate("/wallet")}>
                  <i className="fas fa-wallet me-2"></i>View Wallet
                </button>
                <button className="btn btn-primary rounded-pill px-4" onClick={() => navigate("/alldoctors")}>
                  <i className="fas fa-user-md me-2"></i>Book Token
                </button>
              </div>
            </div>
          ) : (
            <div className="card shadow-lg border-0 rounded-4 overflow-hidden">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-dark sticky-top">
                      <tr>
                        <th className="ps-4 py-3">
                          Sr.No
                        </th>
                        <th className="ps-4 py-3">
                          Date Booked
                        </th>
                        <th className="py-3">Type</th>
                        <th className="py-3">Amount</th>
                        <th className="py-3">Balance</th>
                        <th className="py-3">DR Name/Trans ID</th>
                        <th className="py-3 pe-4">
                          Token No
                        </th>
                      </tr>
                    </thead>
                    <tbody className="table-group-divider">
                      {transactions.map((txn, index) => (
                        <tr key={txn.id}>
                          <td className="ps-4 fw-medium text-muted">
                            {(currentPage - 1) * transactionsPerPage + index + 1}
                          </td>
                          <td className="ps-4 fw-medium">
                            {txn.date}
                          </td>
                          <td>
                            <span className={`badge ${txn.isDebit ? 'bg-danger' : 'bg-success'} text-white px-3 py-2 fs-6 fw-semibold`}>
                              {txn.type}
                            </span>
                          </td>
                          <td>
                            <span className={`h6 fw-bold ${txn.isDebit ? 'text-danger' : 'text-success'}`}>
                              {txn.isDebit ? `-${txn.amount}` : `+${txn.amount}`}
                            </span>
                          </td>
                          <td className="fw-medium">Rs.{txn.balance}</td>
                          <td className="small text-muted">
                            {txn.doctorName}
                          </td>
                          <td className="pe-4 fw-bold h5 text-primary">
                            {txn.tokenNo || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Pagination Controls */}
              {totalRows > 0 && (
                <div className="card-footer bg-light border-0 py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <button 
                      className="btn btn-primary btn-sm rounded-pill"
                      onClick={handleRefresh}
                      disabled={loading}
                    >
                      <i className="fas fa-sync-alt me-1"></i>
                      Refresh
                    </button>
                    <div className="btn-group" role="group">
                      <button 
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <i className="fas fa-chevron-left"></i> Previous
                      </button>
                      <span className="btn btn-outline-secondary btn-sm">
                        in record {(currentPage - 1) * transactionsPerPage + 1}-{Math.min(currentPage * transactionsPerPage, totalRows)}
                      </span>
                      <button 
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next <i className="fas fa-chevron-right"></i>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;
